import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Input } from "@/Components/ui/input";
import { Button } from "@/Components/ui/button";
import { ChevronRight, ArrowLeft } from "lucide-react";
import axiosInstance from '../utils/axiosConfig';
import LanguageSelector from '../Components/Layout/LanguageSelector';
import logo from '../images/pslogo.png';

const ForgotPassword = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [email, setEmail] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');
        setSuccess('');

        try {
            const response = await axiosInstance.post('/auth/forgotpassword', { email });

            if (response.data) {
                setSuccess(t('auth.resetLinkSent'));
                setEmail(''); // Clear the form
            }
        } catch (err) {
            console.log('Forgot password error:', err);

            if (err.response) {
                setError(err.response.data || t('auth.emailNotKnown'));
            } else if (err.request) {
                setError(t('auth.connectionError'));
            } else {
                setError(t('auth.forgotPasswordError'));
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
            <div className="max-w-md w-full space-y-6 p-8 bg-white rounded-lg shadow-lg relative">
                <div className="absolute left-[15px] top-[-5px] w-[80px]">
                    <img src={logo} alt="PS Logo" />
                </div>
                
                {/* Language Selector */}
                <div className="absolute right-4 top-4">
                    <LanguageSelector />
                </div>

                {/* Header Section */}
                <div className="text-center pt-8">
                    <h2 className="text-3xl font-inter text-ps-blue-dark">
                        {t('auth.forgotPasswordTitle')}
                    </h2>
                    <p className="mt-2 text-sm text-gray-600 font-inter">
                        {t('auth.forgotPasswordSubtitle')}
                    </p>
                </div>

                {/* Form Section */}
                <form onSubmit={handleSubmit} className="mt-8 space-y-6">
                    {/* Error Display */}
                    {error && (
                        <div className="text-red-600 text-sm text-center bg-red-50 p-3 rounded font-inter">
                            {error}
                        </div>
                    )}

                    {/* Success Display */}
                    {success && (
                        <div className="text-green-600 text-sm text-center bg-green-50 p-3 rounded font-inter">
                            {success}
                        </div>
                    )}

                    {/* Email Field */}
                    <div>
                        <label className="block text-sm font-inter text-ps-blue-dark mb-2">
                            {t('auth.email')}
                        </label>
                        <Input
                            type="email"
                            required
                            value={email}
                            onChange={(e) => {
                                setEmail(e.target.value);
                                setError('');
                                setSuccess('');
                            }}
                            className="w-full p-2 border rounded bg-white font-inter"
                            placeholder={t('auth.emailPlaceholder')}
                        />
                    </div>

                    {/* Submit Button */}
                    <div>
                        <Button
                            type="submit"
                            disabled={isLoading}
                            className="w-full bg-ps-blue-dark hover:opacity-80 text-white flex items-center justify-center gap-2 h-12 font-inter"
                        >
                            {isLoading ? t('auth.sendingResetLink') : t('auth.sendResetLink')}
                            <ChevronRight className="h-4 w-4" />
                        </Button>
                    </div>

                    {/* Back to Login */}
                    <div className="text-center">
                        <Button
                            type="button"
                            variant="link"
                            onClick={() => navigate('/')}
                            className="text-ps-blue-dark hover:text-ps-blue font-medium flex items-center justify-center gap-2"
                        >
                            <ArrowLeft className="h-4 w-4" />
                            {t('auth.backToLogin')}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ForgotPassword;